// safeJson.js — defensive helpers for the brittle world of preview/staging
// proxies that occasionally return HTML error pages with a 2xx-looking
// status (Cloudflare 502 page, NGINX maintenance page, ingress redirects)
// or empty bodies that crash `await r.json()`.
//
// Ported from the parallel fork's iteration_243 fix (May 2026). The fix
// pattern: wrap `fetch` so callers always receive `{ok, status, data,
// error}` instead of having to remember to `await r.json().catch(() =>
// ({}))` at every call site.
//
// Usage:
//   import { safeFetch, safeJson } from "@/lib/safeJson";
//   const { ok, status, data, error } = await safeFetch(url, options);
//   if (!ok) toast.error(error);

/**
 * Parse a Response object into JSON without ever throwing.
 * Returns `{ok, status, data, error}`:
 *   - ok: response was 2xx AND body parsed cleanly
 *   - data: parsed JSON object (or null on any failure)
 *   - error: human-readable string ready to toast (or null on success)
 */
export async function safeJson(response) {
  if (!response) {
    return { ok: false, status: 0, data: null, error: "No response from server" };
  }
  const status = response.status;
  let text = "";
  try {
    text = await response.text();
  } catch {
    return { ok: false, status, data: null, error: "Couldn't read response body" };
  }
  // Empty body — common for 204 / certain DELETEs.
  if (!text) {
    if (response.ok) return { ok: true, status, data: null, error: null };
    return { ok: false, status, data: null, error: humaniseStatus(status) };
  }
  // HTML body (ingress error page, login redirect, Cloudflare interstitial).
  const trimmed = text.trimStart();
  if (trimmed.startsWith("<")) {
    return {
      ok: false,
      status,
      data: null,
      error: humaniseStatus(status, "Server is busy or restarting — please retry in a moment."),
    };
  }
  // JSON.
  try {
    const data = JSON.parse(text);
    if (response.ok) {
      return { ok: true, status, data, error: null };
    }
    const detail = (data && (data.detail || data.error || data.message)) || humaniseStatus(status);
    return { ok: false, status, data, error: String(detail) };
  } catch {
    return {
      ok: false,
      status,
      data: null,
      error: humaniseStatus(status, "Unexpected response from server."),
    };
  }
}

/**
 * fetch() wrapper that auto-applies `credentials: "include"`, captures
 * network errors, and runs the response through `safeJson`. Returns the
 * same `{ok, status, data, error}` shape — no try/catch needed at the
 * call site.
 */
export async function safeFetch(url, options = {}) {
  try {
    const response = await fetch(url, {
      credentials: "include",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    return await safeJson(response);
  } catch (err) {
    // Network failure / CORS / offline / DNS — never reaches the server.
    return {
      ok: false,
      status: 0,
      data: null,
      error: err?.message ? `Network error: ${err.message}` : "Network error",
    };
  }
}

function humaniseStatus(status, fallback) {
  if (status === 0) return "Network error — please check your connection.";
  if (status === 401) return "You've been signed out — please log in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "Not found.";
  if (status === 409) return "Conflict — that item changed while you were editing.";
  if (status === 422) return "That action isn't allowed in the current state.";
  if (status === 429) return "Too many requests — slow down for a moment.";
  if (status >= 500 && status < 600) {
    return fallback || "Server is busy or restarting — please retry in a moment.";
  }
  return fallback || `Request failed (status ${status}).`;
}
